import React from "react";
import CustomerLogo from "Images/CustomerLogo.png";
import SoppSoppLogo from "Images/SoppSoppLogo.png";
import { Box, Button, ButtonGroup, CircularProgress, Typography, useTheme } from "@mui/material";
import { FileCopyOutlined, TaskAltOutlined } from "@mui/icons-material";
import { toast } from "react-toastify";
import { useRecoilValue } from "recoil";
import { jobIdState } from "atoms/jobIdState.atom";
import { useSelector } from "react-redux";
import { selectCustomerLogo, selectHeaderText, selectSubmissionSubText } from "slices/customerThemeSlice";
import { selectPartialCompletion } from "slices/fnolSubmissionSlice";

const FnolSubmissionComplete = () => {
  const jobId = useRecoilValue(jobIdState);
  const partialCompletion = useSelector(selectPartialCompletion);
  const theme = useTheme();
  const dynamicLogo = useSelector(selectCustomerLogo) || CustomerLogo;
  const headerText = useSelector(selectHeaderText);
  const submissionSubText = useSelector(selectSubmissionSubText);

  // Use jobId if available, then try jobId from submission status, then fall back to fnolId
  const referenceId = jobId || partialCompletion.jobId || partialCompletion.fnolId;
  const isPending = partialCompletion.status === "pending";

  const handleCopy = async () => {
    await navigator.clipboard.writeText(referenceId).then(() => {
      toast.success("Copied To Clipboard", { position: "bottom-center", autoClose: 5000 });
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        height: "100%",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
      }}
    >
      <Box component="img" sx={{ content: `url(${dynamicLogo})`, height: { xs: "40px", sm: "56px" } }}></Box>
      <Typography variant="h1" sx={{ mt: { xs: 2, sm: 4 } }}>
        {headerText}
      </Typography>
      <Box
        sx={{
          display: "flex",
          maxWidth: "600px",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          border: `2px solid ${theme.palette.grey.main}`,
          borderRadius: 3,
          mt: { xs: 4, sm: 5 },
          p: { xs: 2, sm: 5 },
          boxShadow: 2,
        }}
      >

        <TaskAltOutlined sx={{ color: theme.palette.primary.main, fontSize: "72px" }} />

        <Typography variant="h1" component="h2" sx={{ mt: 2 }}>
          Submission Complete
        </Typography>

        <Typography variant="p">
          {submissionSubText}
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            borderRadius: 3,
            backgroundColor: theme.palette.grey.light,
            mt: 2,
            p: { xs: 2, sm: 3 },
          }}
        >
          <Box
            component="img"
            sx={{
              content: `url(${SoppSoppLogo})`,
              width: { xs: "112px", sm: "136px" },
              height: { xs: "16px", sm: "20px" },
            }}
          ></Box>
          {isPending ? (
            <>
              <Box sx={{mt: 2,}}>
                <Typography sx={{ color: "#5D4E37", fontWeight: 600, fontSize: "0.95rem" }}>
                  Just finishing up...
                </Typography>
                <Typography sx={{ color: "#6B5D4D", fontSize: "0.875rem", mt: 0.5 }}>
                  We're completing the final steps of your submission. This usually resolves automatically within <strong>a few minutes</strong>. No action needed from you.
                </Typography>
                <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
                  <CircularProgress size={24} />
                </Box>
              </Box>
            </>
          ) : (
            <>
              <Typography sx={{ mt: 2, color: theme.palette.grey.dark }}>Your Sopp + Sopp Reference Number Is:</Typography>
              <ButtonGroup sx={{ mt: 2 }}>
                <Button
                  sx={{
                    pointerEvents: "none",
                    backgroundColor: theme.palette.secondary.light,
                    borderColor: theme.palette.grey.main,
                    wordBreak: "break-word",
                    borderRadius: 1,
                    color: theme.palette.text.main,
                  }}
                >
                  {referenceId}
                </Button>
                <Button
                  size="small"
                  onClick={handleCopy}
                  sx={{
                    backgroundColor: theme.palette.primary.light,
                    borderColor: theme.palette.grey.main,
                    borderRadius: 1,
                    "&:hover": {
                      backgroundColor: theme.palette.primary.main,
                      "& svg": { color: theme.palette.secondary.light },
                    },
                  }}
                >
                  <FileCopyOutlined sx={{ color: theme.palette.primary.main }} />
                </Button>
              </ButtonGroup>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default FnolSubmissionComplete;
